// Shared helpers for the Stripe Connect onboarding round-trip.
//
// THE native-app rule: Stripe onboarding must
// never dump a talent into Safari. On native we open the hosted onboarding in
// the IN-APP browser sheet (SFSafariViewController via @capacitor/browser), so
// the app stays underneath and the return trip lands back inside the app —
// via universal link when iOS fires it, or via the "Return to Request App"
// hand-off card (custom requestapp:// scheme) when it doesn't. On web the
// behavior is byte-for-byte the old one: a full top-level redirect.
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";

export const IS_NATIVE = Capacitor.isNativePlatform();

/** Open a Stripe-hosted onboarding/refresh URL the right way per platform. */
export async function openStripeUrl(url: string): Promise<void> {
  if (IS_NATIVE) {
    try {
      await Browser.open({ url });
      return;
    } catch {
      // Sheet failed to present (should never happen) — fall through to the
      // web-style redirect so the talent is never stuck on a dead button.
    }
  }
  window.location.href = url;
}

/**
 * Extra body fields for any server call that mints a Stripe account link
 * (register / complete-registration / resume-onboarding). `client: "app"`
 * makes the server tag the link's return_url with `app=1`, which tells the
 * web callback page — if it ever renders inside our in-app browser sheet —
 * to hand the one-time nonce back to the app instead of consuming it in
 * Safari's session. Empty on web so nothing changes there.
 */
export function stripeClientFields(): { client?: string; handoff?: string } {
  // handoff:"callback" (added for build 30) tells /stripe/connect this client
  // can route a ?stripeReturn hand-off through the callback page — the server
  // keeps the legacy /profile round trip for older builds that can't.
  return IS_NATIVE ? { client: "app", handoff: "callback" } : {};
}

/**
 * Subscribe to "the in-app browser sheet was closed" (the talent tapped Done
 * without completing the hand-off). No-op unsubscribe on web. Callers use it
 * to un-stick their pending UI / re-check state.
 */
export function onStripeBrowserClosed(cb: () => void): () => void {
  if (!IS_NATIVE) return () => {};
  const sub = Browser.addListener("browserFinished", cb);
  return () => {
    sub.then((s) => s.remove()).catch(() => {});
  };
}

// ─── App-held completion key (sheet-close self-completion) ──────────────────
// Register/complete-registration responses hand app clients their own copy of
// the one-time nonce (appNonce). We stash it so the APP can finish the round
// trip itself the moment the Stripe sheet closes — never relying on the talent
// tapping the hand-off card. The server forgives a double-finish (a spent
// nonce still identifies its owner), so racing the hand-off is safe.
// Mirrors the pendingAvatarUpload stash (Step6Complete ↔ StripeCallbackPage).
const PENDING_NONCE_KEY = "pendingStripeNonce";
const PENDING_NONCE_TTL_MS = 24 * 60 * 60 * 1000;

export function stashAppStripeNonce(nonce: string): void {
  if (!IS_NATIVE || !nonce) return;
  try {
    localStorage.setItem(PENDING_NONCE_KEY, JSON.stringify({ nonce, ts: Date.now() }));
  } catch {
    // Best-effort: without the stash the hand-off card is still the path.
  }
}

export function getAppStripeNonce(): string | null {
  if (!IS_NATIVE) return null;
  try {
    const raw = localStorage.getItem(PENDING_NONCE_KEY);
    if (!raw) return null;
    const { nonce, ts } = JSON.parse(raw) || {};
    if (!nonce || !ts || Date.now() - ts > PENDING_NONCE_TTL_MS) return null;
    return nonce;
  } catch {
    return null;
  }
}

export function clearAppStripeNonce(): void {
  try {
    localStorage.removeItem(PENDING_NONCE_KEY);
  } catch {}
}

// Deep-link vs sheet-close race guard: DeepLinkHandler marks a Stripe return
// it just routed, so the browserFinished listener (fired by its own
// Browser.close()) doesn't ALSO navigate. Missing the mark is harmless — the
// server tolerates the double spend — this only prevents a visible double
// navigation.
let stripeReturnHandledAt = 0;

export function markStripeReturnHandled(): void {
  stripeReturnHandledAt = Date.now();
}

export function wasStripeReturnJustHandled(windowMs = 5000): boolean {
  return Date.now() - stripeReturnHandledAt < windowMs;
}
